import React, { useState } from 'react';
import { Modal, Button, Input, message, Typography, Space, Radio, Upload, Tooltip, Tabs, Switch, Row, Col, Divider } from 'antd';
import { ApartmentOutlined, UploadOutlined, FileTextOutlined, UnorderedListOutlined, BgColorsOutlined, ClearOutlined, BarChartOutlined } from '@ant-design/icons';
import { useMutation } from 'react-query';
import { sequenceService } from '@/api/sequenceService';
import { parseFasta } from '@/utils/sequenceUtils';
import D3PhylogeneticTree from '@/components/results/phylogenetics/D3PhylogeneticTree';
import WebLogo from '@/components/results/WebLogo';


const { Title, Text, Paragraph } = Typography;


const { TextArea } = Input;

// 请求类型定义
interface SequenceAlignRequest {
  sequences: string[];
  names: string[];
  sequence_type: string;
}

const MultipleSequenceAlignment: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [inputText, setInputText] = useState('');
  const [sequenceType, setSequenceType] = useState('protein');
  const [result, setResult] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('1');
  const [singleLineMode, setSingleLineMode] = useState<boolean>(true);
  const [onlyHighlightDifferences, setOnlyHighlightDifferences] = useState<boolean>(false);

  // 显示模态框
  const showModal = () => {
    setIsModalVisible(true);
  };

  // 多序列比对API调用（需在 handleCancel/handleClear 之前声明，因它们引用 resetMutation）
  const { mutate: alignSequences, isLoading, reset: resetMutation } = useMutation(
    (data: SequenceAlignRequest) => sequenceService.alignSequences(data),
    {
      onSuccess: (data) => {
        setResult(data);
        message.success('比对完成');
      },
      onError: (error: any) => {
        message.error('比对失败: ' + (error.message || '未知错误'));
      },
    }
  );

  // 关闭模态框
  const handleCancel = () => {
    setIsModalVisible(false);
    // 关闭时重置状态，避免下次打开残留
    setResult(null);
    setInputText('');
    setActiveTab('1');
    resetMutation();
  };

  // 清除输入与比对结果，回到输入状态
  const handleClear = () => {
    setInputText('');
    setResult(null);
    setActiveTab('1');
    resetMutation(); // 重置 react-query mutation 状态，防止缓存残留
  };

  // 处理表单提交
  const handleSubmit = () => {
    if (!inputText.trim()) {
      message.warning('请输入序列');
      return;
    }

    try {
      const parsedSequences = parseFasta(inputText);
      
      if (parsedSequences.length < 2) {
        message.error('请输入至少两条FASTA格式的序列进行比对');
        return;
      }

      // 保存原始ID用于下载和显示
      const originalIds = parsedSequences.map(p => p.originalId);

      const data: SequenceAlignRequest = {
        sequences: parsedSequences.map(p => p.sequence),
        names: originalIds,
        sequence_type: sequenceType,
      };

      alignSequences(data);
    } catch (error) {
      console.error('处理序列出错:', error);
      message.error('处理序列出错，请检查输入格式');
    }
  };

  // 下载结果
  const handleDownload = (format: 'fasta' | 'newick' | 'phyloxml') => {
    if (!result) return;

    try {
      let content = '';
      let filename = '';
      let mimeType = 'text/plain';

      if (format === 'fasta' && result.aligned_sequences && result.names) {
        content = result.aligned_sequences.map((seq: string, i: number) => 
          `${result.names[i]}\n${seq}`
        ).join('\n');
        filename = 'alignment_result.fasta';
      } else if (format === 'newick' && result.newick_tree) {
        content = result.newick_tree;
        filename = 'phylogenetic_tree.newick';
      } else if (format === 'phyloxml' && result.phyloxml_tree) {
        content = result.phyloxml_tree;
        filename = 'phylogenetic_tree.xml';
        mimeType = 'application/xml';
      } else {
        message.error('没有可下载的数据');
        return;
      }

      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      message.success(`已下载 ${filename}`);
    } catch (error) {
      console.error('下载失败:', error);
      message.error('下载失败');
    }
  };

  // 渲染比对结果
  const renderAlignment = () => {
    if (!result || !result.aligned_sequences || !result.names) {
      return <Text>无比对结果</Text>;
    }

    // 获取颜色方案
    const getColorScheme = (char: string, charIndex: number): string => {
      // 统计该位置所有序列的字符频率
      const charCounts: Record<string, number> = {};
      result.aligned_sequences.forEach((seq: string) => {
        const c = seq[charIndex];
        charCounts[c] = (charCounts[c] || 0) + 1;
      });

      // 找出最频繁的字符
      let maxCount = 0;
      let mostFrequentChar = '';
      Object.entries(charCounts).forEach(([c, count]) => {
        if (count > maxCount) {
          maxCount = count;
          mostFrequentChar = c;
        }
      });

      // 如果所有字符都相同，不着色
      if (Object.keys(charCounts).length === 1) {
        return 'transparent';
      }

      // 空位(-)总是用红色背景
      if (char === '-') {
        return '#FF6B6B';
      }

      // 如果当前字符是最频繁的字符，不着色
      if (char === mostFrequentChar) {
        return 'transparent';
      }

      // 如果启用了"只标记不一致"模式，进行额外检查
      if (onlyHighlightDifferences) {
        // 检查该位置是否所有序列都相同
        const allSame = result.aligned_sequences.every((seq: string) => 
          seq[charIndex] === result.aligned_sequences[0][charIndex]
        );
        
        if (allSame) {
          return 'transparent'; // 如果所有序列该位置都相同，不标记颜色
        }
      }

      // 获取该位置的所有少数字符，并为每组分配颜色
      const minorityChars = Object.keys(charCounts).filter(c => c !== mostFrequentChar && c !== '-');
      const minorityColors = ['#FFB366', '#66B3FF', '#66FF66', '#FF66B3', '#B366FF', '#FFFF66', '#66FFFF', '#FF6666'];
      
      const charIndex_in_minorities = minorityChars.indexOf(char);
      if (charIndex_in_minorities >= 0) {
        return minorityColors[charIndex_in_minorities % minorityColors.length];
      }

      return 'transparent';
    };

    // 渲染单行模式的序列
    const renderSingleLineMode = (seq: string, index: number) => (
      <div key={index} style={{ marginBottom: '8px', display: 'flex' }}>
        <Text strong style={{ 
          minWidth: '100px', 
          marginRight: '10px', 
          overflow: 'hidden', 
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {result.names[index].replace('>', '')}
        </Text>
        <div style={{ flex: 1 }}>
          {seq.split('').map((char: string, charIndex: number) => (
            <span key={charIndex} style={{ 
              backgroundColor: getColorScheme(char, charIndex), 
              padding: '1px 0',
              display: 'inline-block',
              width: '10px',
              textAlign: 'center',
              color: char === '-' ? '#aaa' : '#000'
            }}>
              {char}
            </span>
          ))}
        </div>
      </div>
    );

    // 渲染自适应换行模式的序列
    const renderWrappedMode = (seq: string, index: number) => {
      // 将序列分成多个片段，每个片段占据一行
      const chunks: string[] = [];
      const chunkSize = 60; // 假设每行显示60个字符
      
      for (let i = 0; i < seq.length; i += chunkSize) {
        chunks.push(seq.substring(i, i + chunkSize));
      }

      return (
        <div key={index} style={{ marginBottom: '16px' }}>
          <Text strong>{result.names[index].replace('>', '')}</Text>
          {chunks.map((chunk, chunkIndex) => (
            <div key={chunkIndex} style={{ marginTop: '4px' }}>
              {chunk.split('').map((char: string, charIndex: number) => {
                const globalCharIndex = chunkIndex * chunkSize + charIndex;
                return (
                  <span key={charIndex} style={{ 
                    backgroundColor: getColorScheme(char, globalCharIndex), 
                    padding: '1px 0',
                    display: 'inline-block',
                    width: '10px',
                    textAlign: 'center',
                    color: char === '-' ? '#aaa' : '#000'
                  }}>
                    {char}
                  </span>
                );
              })}
            </div>
          ))}
        </div>
      );
    };

    // 渲染按块分组的序列（新的显示方式）
    const renderBlockMode = () => {
      if (!result.aligned_sequences || !result.aligned_sequences.length) return null;

      const sequences = result.aligned_sequences;
      const names = result.names;
      const seqLength = sequences[0].length;
      const blockSize = 60; // 每块显示的字符数
      const totalBlocks = Math.ceil(seqLength / blockSize);
      const blocks = [];

      // 创建所有的块
      for (let blockIndex = 0; blockIndex < totalBlocks; blockIndex++) {
        const startPos = blockIndex * blockSize;
        const endPos = Math.min((blockIndex + 1) * blockSize, seqLength);
        
        // 创建一个块，包含所有序列在这个范围内的片段
        const block = (
          <div key={`block-${blockIndex}`} style={{ marginBottom: '20px' }}>
            <Text type="secondary">位置: {startPos + 1}-{endPos}</Text>
            {sequences.map((seq: string, seqIndex: number) => {
              const chunk = seq.substring(startPos, endPos);
              return (
                <div key={`seq-${seqIndex}-block-${blockIndex}`} style={{ marginBottom: '8px', display: 'flex' }}>
                  <Text strong style={{ 
                    minWidth: '100px', 
                    marginRight: '10px', 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {names[seqIndex].replace('>', '')}
                  </Text>
                  <div>
                    {chunk.split('').map((char: string, charIndex: number) => {
                      const globalCharIndex = startPos + charIndex;
                      return (
                        <span key={charIndex} style={{ 
                          backgroundColor: getColorScheme(char, globalCharIndex), 
                          padding: '1px 0',
                          display: 'inline-block',
                          width: '10px',
                          textAlign: 'center',
                          color: char === '-' ? '#aaa' : '#000'
                        }}>
                          {char}
                        </span>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        );
        
        blocks.push(block);
      }

      return <div>{blocks}</div>;
    };

    return (
      <div>
        <Space direction="vertical" style={{ width: '100%', marginBottom: '16px' }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Button 
                type="primary" 
                icon={<FileTextOutlined />} 
                onClick={() => handleDownload('fasta')}
              >
                下载FASTA格式比对结果
              </Button>
            </Col>
            <Col>
              <Space>
                <Text>显示方式：</Text>
                <Switch
                  checkedChildren={<UnorderedListOutlined />}
                  unCheckedChildren={<BgColorsOutlined />}
                  checked={singleLineMode}
                  onChange={(checked) => setSingleLineMode(checked)}
                />
                <Text>{singleLineMode ? '单行显示' : '换行显示'}</Text>
                <Divider type="vertical" />
              </Space>
            </Col>
          </Row>
          <Text type="secondary">序列总数: {result.aligned_sequences.length}, 序列长度: {result.aligned_sequences[0]?.length || 0}</Text>
        </Space>
        
        <div style={{ 
          fontFamily: 'monospace', 
          whiteSpace: 'pre', 
          overflowX: 'auto', 
          background: '#f5f5f5', 
          padding: '16px', 
          borderRadius: '4px',
          border: '1px solid #e8e8e8',
          height: '500px',
          overflowY: 'auto',
          marginTop: 8,
          width: '100%',
          maxWidth: '100%'
        }}>
          {singleLineMode 
            ? result.aligned_sequences.map((seq: string, index: number) => renderSingleLineMode(seq, index))
            : renderBlockMode()
          }
        </div>
      </div>
    );
  };

  // 渲染WebLogo
  const renderWebLogo = () => {
    if (!result || !result.aligned_sequences) {
      return <Text>无比对结果</Text>;
    }

    return (
      <div>
        <Space direction="vertical" style={{ width: '100%', marginBottom: '16px' }}>
          <Text type="secondary">
            WebLogo显示序列保守性，字符高度表示该位置的信息内容。颜色编码：
            {sequenceType === 'protein' ? 
              '极性(绿)、酸性(红)、碱性(蓝)、疏水性(黑)、极性不带电(紫)' : 
              'A(红)、T/U(蓝)、G(橙)、C(绿)'
            }
          </Text>
        </Space>
        
        <WebLogo 
          sequences={result.aligned_sequences}
          sequenceType={sequenceType as 'protein' | 'dna' | 'rna'}
          width={Math.max(1200, result.aligned_sequences[0]?.length * 80 || 1200)}
          height={600}
        />
      </div>
    );
  };

  // 渲染进化树
  const renderTree = () => {
    if (!result || (!result.newick_tree && !result.phyloxml_tree)) {
      return <Text>无进化树数据</Text>;
    }

    // 检查是否有进化树数据
    const hasNewickTree = result.newick_tree && typeof result.newick_tree === 'string';
    const hasPhyloXMLTree = result.phyloxml_tree && typeof result.phyloxml_tree === 'string';

    return (
      <div>
        <Space direction="vertical" style={{ width: '100%', marginBottom: '16px' }}>
          <Space>
            {hasNewickTree && (
              <Button 
                type="primary" 
                icon={<FileTextOutlined />} 
                onClick={() => handleDownload('newick')}
              >
                下载Newick格式
              </Button>
            )}
            {hasPhyloXMLTree && (
              <Button 
                type="primary" 
                icon={<FileTextOutlined />} 
                onClick={() => handleDownload('phyloxml')}
              >
                下载PhyloXML格式
              </Button>
            )}
          </Space>
          <Text type="secondary">使用D3.js库从头实现的进化树可视化，支持多种布局方式和交互功能。</Text>
        </Space>

        <div style={{ border: '1px solid #f0f0f0', borderRadius: '4px', overflow: 'auto' }}>
          <D3PhylogeneticTree
            newickString={result.newick_tree || ''}
            phyloXmlString={result.phyloxml_tree || ''}
            width={750}
            height={500}
            showControls={true}
          />
        </div>
      </div>
    );
  };

  // 使用说明文本
  const instructionsText = `使用说明:
1. 请输入FASTA格式的多条序列，如:
>seq1
ATCGATCG
>seq2
ATCGGGCG
2. 选择序列类型（蛋白质、DNA或RNA）
3. 点击"开始比对"按钮执行多序列比对`;

  // 标签页项
  const tabItems = [
    {
      key: '1',
      label: (
        <span>
          <FileTextOutlined /> 序列比对
        </span>
      ),
      children: renderAlignment(),
    },
    {
      key: '2',
      label: (
        <span>
          <BarChartOutlined /> WebLogo
        </span>
      ),
      children: renderWebLogo(),
    },
    {
      key: '3',
      label: (
        <span>
          <ApartmentOutlined /> 进化树
        </span>
      ),
      children: renderTree(),
    }
  ];

  return (
    <>
      <Button onClick={showModal} icon={<ApartmentOutlined />}>
        多序列比对
      </Button>

      <Modal
        title="多序列比对"
        open={isModalVisible}
        onCancel={handleCancel}
        width={1200}
        centered
        bodyStyle={{ padding: 16, height: '80vh', overflow: 'auto' }}
        footer={null}
      >
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* 主内容区域 */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {!result ? (
              // 输入区域
              <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Text strong style={{ marginBottom: 8 }}>输入序列：</Text>
                <TextArea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder={instructionsText}
                  style={{ flex: 1, resize: 'none' }}
                />
              </div>
            ) : (
              // 结果显示区域
              <div style={{ flex: 1, minHeight: 0 }}>
                <Tabs 
                  activeKey={activeTab} 
                  onChange={setActiveTab}
                  items={tabItems}
                  style={{ height: '100%' }}
                  tabBarStyle={{ marginBottom: '8px' }}
                />
              </div>
            )}
          </div>
          
          {/* 底部按钮栏 */}
          <div style={{ 
            borderTop: '1px solid #f0f0f0', 
            paddingTop: 16,
            background: '#fafafa',
            margin: '0 -16px -16px -16px',
            padding: '12px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <Text strong style={{ marginRight: 8 }}>序列类型：</Text>
              <Radio.Group 
                value={sequenceType} 
                onChange={(e) => setSequenceType(e.target.value)}
              >
                <Radio.Button value="protein">蛋白质</Radio.Button>
                <Radio.Button value="dna">DNA</Radio.Button>
                <Radio.Button value="rna">RNA</Radio.Button>
              </Radio.Group>
            </div>
            
            <Space>
              <Upload
                beforeUpload={file => {
                  const reader = new FileReader();
                  reader.onload = e => {
                    const content = e.target?.result as string;
                    setInputText(content);
                    message.success(`${file.name} 已成功读取，请点击"开始比对"按钮执行比对`);
                  };
                  reader.readAsText(file);
                  return false;
                }}
                showUploadList={false}
              >
                <Button icon={<UploadOutlined />}>上传文件</Button>
              </Upload>
              
              <Button 
                onClick={handleClear}
                icon={<ClearOutlined />}
              >
                清除
              </Button>
              
              <Button 
                type="primary" 
                onClick={handleSubmit} 
                loading={isLoading}
              >
                开始比对
              </Button>
            </Space>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default MultipleSequenceAlignment; 